import * as React from "react";

import { AgentThreadsTable } from "@/Agents/Threads/AgentThreadsTable";
import { LaunchAgentMenu } from "@/shared/components/agentThread/LaunchAgentMenu";
import type { Filter } from "@/shared/components/ResourceView";

import { useDatasetContext } from "../useDatasetContext";

import styles from "./DatasetAgentThreads.module.css";

/**
 * "Agents" tab on the dataset-detail route (labeled "Agents" to mirror the
 * "Actions" tab and conserve horizontal space; it lists the dataset's agent
 * threads). Renders the shared
 * :class:`AgentThreadsTable` pinned to the current dataset via a
 * ``subject_id`` scope filter; the backend renders this as a
 * correlated ``EXISTS`` subquery against ``agent_thread_subjects`` so
 * the table shows every thread whose goal target or
 * ``ClientViewingContext`` ever referenced this dataset.
 *
 * The scope is enforced server-side and cannot be widened by the
 * user's filter widget. Even if the user picks ``OR`` match mode for
 * their widget filters, ``composeFilters`` wraps the scope and the
 * user filters in an outer AND so the dataset pin survives.
 *
 * A :class:`LaunchAgentMenu` for this dataset rides in the table's
 * ``trailingActions`` slot — same row as the Filter / Columns controls,
 * pushed to the right by the slot's ``margin-left: auto`` — so the
 * affordance sits next to the table chrome rather than on its own
 * empty toolbar row. The button renders nothing when the caller's
 * org has no agents declaring a ``DATASET_ID`` variable.
 *
 * Rendered as the dataset detail view's "Agents" tab.
 */
export function DatasetAgentThreads() {
  const { datasetId } = useDatasetContext();

  const scope = React.useMemo<Filter[]>(
    () => [
      {
        type: "string",
        field: "subject_id",
        comparator: "EQUALS",
        values: [datasetId],
      },
    ],
    [datasetId],
  );

  return (
    <div className={styles["agent-threads-tab"]}>
      <AgentThreadsTable
        scope={scope}
        noData={{ title: "No threads for this dataset yet" }}
        trailingActions={
          <LaunchAgentMenu subject={{ kind: "dataset", id: datasetId }} />
        }
      />
    </div>
  );
}
